import { Link } from 'react-router-dom';

import { DEFAULT_PAGE_LIMIT, ENDPOINTS, type StaleJudgementItem } from '@clm/shared-types';

import { fetchStaleJudgements } from '../../api/quality';
import { CursorPager } from '../../components/CursorPager';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { PanelProblem, Tile } from './panel';
import { FORBIDDEN_COPY, usePaging, usePanel } from './panel-state';

/**
 * (d) Stale judgements — `vw_clause_search_judgement WHERE is_stale`, oldest first.
 *
 * A judgement recorded against a clause version that has since been revised
 * still counts, at the weight the view gives it (0.3, printed because FR-27
 * says the number the ranking uses is shown). No Re-confirm or Retract here: a
 * judgement is re-confirmed or retracted where judgements are recorded — a fresh
 * judgement on the current version retires the same reviewer's stale rows for
 * that query, so this queue drains as reviewers re-judge.
 */

const PANEL = 'Stale judgements';
const ENDPOINT_PATH = ENDPOINTS['quality-stale-judgements'].path;

const COLUMNS: readonly DataTableColumn<StaleJudgementItem>[] = [
  { id: 'query', header: 'Query', cell: (r) => r.query_text },
  {
    id: 'clause',
    header: 'Clause',
    cell: (r) => <Link to={`/clauses/${encodeURIComponent(r.clause_code)}`}>{r.clause_code}</Link>,
  },
  { id: 'judged', header: 'Judged against', cell: (r) => `v${String(r.judged_version_no)}` },
  {
    id: 'current',
    header: 'Current',
    cell: (r) => (r.current_version_no === null ? 'retired' : `v${String(r.current_version_no)}`),
  },
  { id: 'judgement', header: 'Judgement', cell: (r) => r.judgement },
  { id: 'by', header: 'By', cell: (r) => r.judged_by_upn },
  { id: 'when', header: 'When', cell: (r) => r.judged_utc },
  { id: 'weight', header: 'Weight', cell: (r) => r.staleness_weight, cellClassName: 'num' },
];

export function StaleJudgementsPanel() {
  const paging = usePaging();
  const { state, reload } = usePanel(
    (abort) =>
      fetchStaleJudgements(
        { page: paging.cursor === undefined ? { limit: DEFAULT_PAGE_LIMIT } : { limit: DEFAULT_PAGE_LIMIT, cursor: paging.cursor } },
        abort,
      ),
    [paging.cursor],
  );

  return (
    <section className="card qpanel" aria-labelledby="q-stale">
      <h3 id="q-stale">{PANEL}</h3>
      <p>
        Active judgements recorded against clause text that has since been revised. They still steer ranking,
        at the reduced weight shown, until a reviewer re-judges the query against the current text or retracts
        them — both happen where judgements are recorded, not here.
      </p>

      {state.kind === 'loading' ? (
        <p role="status" aria-busy="true">
          Reading {PANEL}…
        </p>
      ) : null}

      {state.kind === 'problem' ? (
        <PanelProblem panel={PANEL} state={state} endpointPath={ENDPOINT_PATH} onRetry={reload} forbiddenCopy={FORBIDDEN_COPY} />
      ) : null}

      {state.kind === 'ready' ? (
        <>
          <div className="totals">
            <Tile label="Stale" value={state.response.totals.stale} tone={state.response.totals.stale > 0 ? 'warn' : undefined} />
            <Tile label="Active judgements" value={state.response.totals.active} />
            <Tile label="Oldest stale" value={state.response.totals.oldest_stale_judged_utc ?? '—'} />
          </div>
          <DataTable
            caption="Judgements recorded against superseded clause text"
            columns={COLUMNS}
            rows={state.response.items}
            rowKey={(r) => r.feedback_id}
            empty={
              state.response.totals.active === 0 ? (
                <p>No judgements have been recorded yet.</p>
              ) : (
                <p>No judgement is stale — every active judgement was recorded against the current text.</p>
              )
            }
          />
          <CursorPager
            next_cursor={state.response.page.next_cursor}
            historyDepth={paging.historyDepth}
            onNext={() => {
              if (state.response.page.next_cursor !== null) paging.next(state.response.page.next_cursor);
            }}
            onPrevious={paging.previous}
            pageNumber={paging.page}
            returnedCount={state.response.page.returned_count}
            totalInScope={null}
          />
        </>
      ) : null}
    </section>
  );
}
